/**
 * features/parent/ParentPortal.tsx
 * ------------------------------------------------------------------
 * Phase 2 — Parent / Guardian portal.
 *
 * Read-only view of linked children: academic reports, attendance,
 * profile details, and (optionally) discipline records.
 *
 * The parent sees only the students linked via their
 * `AppUser.parentAccount.studentIds` array.
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import {
  GraduationCap,
  FileText,
  Calendar,
  User,
  ShieldAlert,
  Award,
  Download,
  AlertCircle,
  // Round 12: live academic tab
  BarChart3,
  // Communications
  MessageSquare,
  // Broadcast notices
  Megaphone, AlertTriangle,
  NotebookPen, Library, CalendarClock, CalendarDays, ListChecks } from 'lucide-react';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useParentAcademicHold, ParentHoldNotice } from '@/features/parent/academicHold';
import { useParentPortalControls, ParentNoticeBanner, ParentMaintenanceScreen } from '@/features/parent/parentPortalControls';
import { useAuth } from '@/contexts/AuthContext';
import { toLegacyTotals } from '@/lib/attendanceCalc';
import StudentAvatar from '@/components/ui/StudentAvatar';
import ChildPhotoCard from '@/features/parent/ChildPhotoCard';
import PhotoNeededCard from '@/features/parent/PhotoNeededCard';
import DocumentsTab from '@/features/documents/DocumentsTab';
// Round 13: parent curriculum view
// Parent ↔ school communications
import ParentMessages from '@/features/communications/ParentMessages';
// Broadcast notices (school -> parent)
import ParentNoticesFeed from '@/features/parent/ParentNoticesFeed';
import ParentPushPrompt from '@/features/parent/ParentPushPrompt';
// Phase 4: prompt to complete missing guardian-contact fields
import GuardianContactPrompt from '@/features/parent/GuardianContactPrompt';
// Add-a-sibling by registration code
import AddChildCard from '@/features/parent/AddChildCard';
import { useParentNotices } from '@/features/communications/useParentNotices';
// Round 12: shared academic dashboard
import AcademicPerformance from '@/components/AcademicPerformance';
import { GradientHero, SoftCard, StatChip } from '@/features/finance/ui';
import { PupilBooks } from '@/features/textbooks/PupilBooks';
import ExamTimetableView from '@/features/exams/ExamTimetableView';
import MyTimetable from '@/features/timetable/MyTimetable';
import { useExamTimetable, useMinuteClock } from '@/features/exams/useExamTimetable';
import SubjectChoices from '@/features/parent/SubjectChoices';
import { SETTINGS_DOC as CHOICE_SETTINGS, levelForClass, settingsFrom as choiceSettingsFrom, type SubjectChoiceSettings } from '@/lib/subjectChoices';
import { Spinner } from '@/components/ui/primitives';
import type {
  GeneratedReport,
  DailyAttendanceDoc,
  DisciplineRecord,
  AchievementRecord,
  SchoolSettings,
  // Round 12
  MarksDocument,
} from '@/types';

interface ChildAcademics {
  ok: boolean;
  reports: GeneratedReport[];
  disciplineRecords: DisciplineRecord[];
  achievementRecords: AchievementRecord[];
  attendanceDocs: DailyAttendanceDoc[];
  marksDocs: MarksDocument[];
  classAvgBySubject: Record<string, number>;
  /** The child's homework from the last two weeks on, without the class
   *  roster (functions/lib/childHomework.js). Absent from an older deploy. */
  homework?: ParentHomework[];
}

/** A homework item as getChildAcademics hands it to a parent: no roster. */
interface ParentHomework {
  id: string;
  title: string;
  instructions: string;
  kind: 'task' | 'handout';
  handIn: 'upload' | 'tick';
  subjectName: string;
  setOn: string;
  dueDate: string;
  setByName: string;
  handedIn: boolean;
  ticked: boolean;
}

/** '2026-11-04' -> 'Wed 4 Nov'. */
const longDate = (iso: string): string =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '';

/* ==================================================================
   COMPONENT
   ================================================================== */

export default function ParentPortal() {
  const { students, classes, subjects, teachers, termStatuses } = useData();
  const { userData } = useAuth();
  const { heldFor, ready: holdReady } = useParentAcademicHold();
  const portal = useParentPortalControls();

  // Account-level broadcast notices (not per-child). Owned here so the
  // tab badge and the feed share a single listener.
  const noticesCtl = useParentNotices(userData?.uid, userData?.displayName);

  const [selectedChild, setSelectedChild] = useState<string | null>(null);

  /* DEEP-LINK FROM THE DASHBOARD. The parent's first screen carries a
     Reports button per child; it drops a one-shot marker (the same
     sessionStorage convention Shell uses for flag notifications) and this
     picks it up. One-shot on purpose: coming back to the portal later should
     land where the parent left it, not keep re-opening Reports. */
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('lkc.parentPortalTarget');
      if (!raw) return;
      sessionStorage.removeItem('lkc.parentPortalTarget');
      const t = JSON.parse(raw) as { studentId?: string; tab?: string };
      if (t.studentId) setSelectedChild(t.studentId);
      if (t.tab) setActiveTab(t.tab as typeof activeTab);
    } catch {
      /* a malformed marker must never stop the portal rendering */
    }
  }, []);
  const [activeTab, setActiveTab] = useState<'academic' | 'homework' | 'books' | 'exams' | 'timetable' | 'subjects' | 'reports' | 'attendance' | 'profile' | 'achievements' | 'discipline' | 'documents' | 'messages' | 'notices'>('academic');

  /* Whether the CHILD currently being viewed is paused for fees. Not the
     parent — a parent is not the unit being held. */
  const childHeld = holdReady && !!selectedChild && heldFor(selectedChild);
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [attendanceDocs, setAttendanceDocs] = useState<DailyAttendanceDoc[]>([]);
  const [disciplineRecords, setDisciplineRecords] = useState<DisciplineRecord[]>([]);
  const [achievementRecords, setAchievementRecords] = useState<AchievementRecord[]>([]);
  // Round 12: marks + term-status for the published Academic view.
  const [marksDocs, setMarksDocs] = useState<MarksDocument[]>([]);
  const [classAvgBySubject, setClassAvgBySubject] = useState<Record<string, number>>({});
  const [homework, setHomework] = useState<ParentHomework[]>([]);
  const [settings, setSettings] = useState<SchoolSettings | null>(null);
  const [loading, setLoading] = useState(true);
  /* WHY THE PAGE IS EMPTY, WHEN IT IS EMPTY FOR A REASON.
     This used to be swallowed into console.error, so a parent whose call
     failed was shown "No reports available yet for this student" — the
     school telling them their child's reports do not exist when in fact the
     server never answered. Doreen Motshegwa sat on that screen for two
     hours during the outage of 25 Sep 2026 believing exactly that, and the
     school went looking for a missing report that was there all along. */
  const [loadError, setLoadError] = useState<string | null>(null);

  // Get linked student IDs
  const linkedStudentIds = useMemo(
    () => userData?.parentAccount?.studentIds ?? [],
    [userData],
  );

  const linkedStudents = useMemo(
    () => students.filter((s) => linkedStudentIds.includes(s.id)),
    [students, linkedStudentIds],
  );

  const child = useMemo(
    () => linkedStudents.find((s) => s.id === selectedChild) ?? null,
    [linkedStudents, selectedChild],
  );

  const classObj = useMemo(
    () => (child ? classes.find((c) => c.id === child.currentClass) : null),
    [child, classes],
  );

  /* The child's exam timetable (from Al-Nur, 30 Oct 2026): the papers on their
     statement for every PUBLISHED series that includes their class. */
  const exams = useExamTimetable({ studentId: child?.id, className: classObj?.name, pupilId: child?.id, enabled: !!child });
  const examClock = useMinuteClock();
  const hasExams = exams.sittings.length > 0;

  /* NEXT YEAR'S SUBJECTS (1 Nov 2026): a Form 3 child's IGCSE choices, a
     Form 5 child's AS choices, while the office has the window open — and
     after it closes, so the family can still see what they sent. */
  const [choiceRaw, setChoiceRaw] = useState<Partial<SubjectChoiceSettings> | null>(null);
  useEffect(() => {
    getDoc(doc(db, COLLECTIONS.schoolSettings, CHOICE_SETTINGS))
      .then((s) => setChoiceRaw(s.exists() ? (s.data() as Partial<SubjectChoiceSettings>) : null))
      .catch(() => setChoiceRaw(null));
  }, []);
  const choiceSettings = useMemo(() => choiceSettingsFrom(choiceRaw, subjects), [choiceRaw, subjects]);
  const choiceLevel = classObj && choiceSettings.open ? levelForClass(choiceSettings, classObj.name) : null;

  // Auto-select first child
  useEffect(() => {
    if (!selectedChild && linkedStudents.length > 0) {
      setSelectedChild(linkedStudents[0].id);
    }
  }, [linkedStudents, selectedChild]);

  // Load school settings (discipline visibility toggle)
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, COLLECTIONS.schoolSettings, 'default'));
        if (snap.exists()) setSettings(snap.data() as SchoolSettings);
        else setSettings({ id: 'default', parentDisciplineVisible: true });
      } catch {
        setSettings({ id: 'default', parentDisciplineVisible: true });
      }
    })();
  }, []);

  // Load data when child changes — via the getChildAcademics callable,
  // which returns ONLY this child's slice (marks/attendance are stored
  // as class-level docs server-side; the function strips them to the
  // child) plus an anonymous per-subject class average.
  useEffect(() => {
    if (!selectedChild) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        setLoadError(null);
        const fn = httpsCallable(functions, 'getChildAcademics');
        const res = (await fn({ studentId: selectedChild })) as { data: ChildAcademics };
        if (cancelled) return;
        const d = res.data;
        setReports([...d.reports].sort((a, b) => (b.generatedAt ?? '').localeCompare(a.generatedAt ?? '')));
        setAttendanceDocs(d.attendanceDocs ?? []);
        setDisciplineRecords([...d.disciplineRecords].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')));
        setAchievementRecords([...(d.achievementRecords ?? [])].sort((a, b) => (b.awardDate ?? b.createdAt ?? '').localeCompare(a.awardDate ?? a.createdAt ?? '')));
        setMarksDocs(d.marksDocs ?? []);
        setClassAvgBySubject(d.classAvgBySubject ?? {});
        setHomework(d.homework ?? []);
      } catch (e) {
        console.error('Parent portal load error:', e);
        if (cancelled) return;
        /* The fee-hold refusal is written for a parent to read, so it is
           passed through as it stands. Anything else is a fault on our side
           and says so, rather than being dressed up as an empty record. */
        const raw = (e as { code?: string; message?: string });
        setLoadError(
          raw?.code === 'functions/permission-denied' && raw.message
            ? raw.message
            : 'We could not load your child\u2019s information just now. '
              + 'Please refresh the page. If it keeps happening, contact the school office.',
        );
        setReports([]);
        setAttendanceDocs([]);
        setDisciplineRecords([]);
        setAchievementRecords([]);
        setMarksDocs([]);
        setHomework([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedChild]);

  // Attendance stats
  const attendanceStats = useMemo(() => {
    if (!selectedChild || attendanceDocs.length === 0)
      return { present: 0, absent: 0, total: 0, rate: 0 };
    const totals = toLegacyTotals(attendanceDocs, [selectedChild]);
    const rec = totals[selectedChild];
    if (!rec) return { present: 0, absent: 0, total: 0, rate: 0 };
    // Count total school days this student appears in any record
    let totalDays = 0;
    for (const d of attendanceDocs) {
      if (d.entries && selectedChild in d.entries) totalDays++;
    }
    const absent = rec.daysAbsent;
    const present = totalDays - absent;
    return {
      present,
      absent,
      total: totalDays,
      rate: totalDays > 0 ? Math.round((present / totalDays) * 100) : 0,
    };
  }, [selectedChild, attendanceDocs]);

  const showDiscipline = settings?.parentDisciplineVisible !== false;

  type PortalTab = 'academic' | 'homework' | 'books' | 'exams' | 'timetable' | 'subjects' | 'reports' | 'attendance' | 'profile' | 'achievements' | 'discipline' | 'documents' | 'messages' | 'notices';

  const tabs = useMemo(() => {
    const t: Array<{ id: PortalTab; label: string; icon: typeof FileText }> = [
      // Round 12: live academic dashboard first — gives parents the
      // current-term snapshot before they dig into PDFs.
      { id: 'academic', label: 'Academic', icon: BarChart3 },
      { id: 'homework', label: 'Homework', icon: NotebookPen },
      { id: 'books', label: 'Books', icon: Library },
      { id: 'timetable', label: 'Timetable', icon: CalendarDays },
      // Only for a child sitting exams, and near the front while they are.
      ...(hasExams ? [{ id: 'exams' as PortalTab, label: 'Exams', icon: CalendarClock }] : []),
      ...(choiceLevel ? [{ id: 'subjects' as PortalTab, label: `${choiceSettings.year} subjects`, icon: ListChecks }] : []),
      { id: 'reports', label: 'Reports', icon: FileText },
      { id: 'attendance', label: 'Attendance', icon: Calendar },
      { id: 'profile', label: 'Profile', icon: User },
      { id: 'achievements', label: 'Achievements', icon: Award },
      { id: 'documents', label: 'Documents', icon: FileText },
      // School -> parent broadcast notices (account-level)
      { id: 'notices', label: 'Notices', icon: Megaphone },
      { id: 'messages', label: 'Messages', icon: MessageSquare },
    ];
    if (showDiscipline) {
      t.push({ id: 'discipline', label: 'Discipline', icon: ShieldAlert });
    }
    return t;
  }, [showDiscipline, hasExams, choiceLevel, choiceSettings.year]);

  // (classmates roster no longer needed: class averages come from the
  // getChildAcademics callable as an anonymous aggregate.)

  if (linkedStudents.length === 0) {
    return (
      <div className="space-y-6">
        <GradientHero
          icon={GraduationCap}
          title="Parent Portal"
          subtitle="View your children's academic information"
        />
        <SoftCard>
          <div className="flex items-center gap-3 text-gray-500">
            <AlertCircle size={20} />
            <p>No children are linked to your account yet. Please contact the school administration.</p>
          </div>
        </SoftCard>
      </div>
    );
  }

  if (portal.maintenance.enabled) {
    return <ParentMaintenanceScreen message={portal.maintenance.message} />;
  }
  if (!holdReady) {
    return <div className="py-16 text-center text-sm text-gray-500">Loading…</div>;
  }
  return (
    <div className="space-y-6">
      <GradientHero
        icon={GraduationCap}
        title="Parent Portal"
        subtitle="View your children's academic information"
      />

      {portal.notice.enabled && <ParentNoticeBanner message={portal.notice.message} />}

      {/* Children selector (if more than one) */}
      {linkedStudents.length > 1 && (
        <div className="flex gap-3 flex-wrap">
          {linkedStudents.map((s) => {
            const cls = classes.find((c) => c.id === s.currentClass);
            const isActive = s.id === selectedChild;
            return (
              <button
                key={s.id}
                onClick={() => { setSelectedChild(s.id); setActiveTab('academic'); }}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                  isActive
                    ? 'bg-white dark:bg-gray-800 border-primary shadow-md'
                    : 'bg-white/50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <StudentAvatar student={s} size="sm" />
                <div className="text-left">
                  <p className={`text-sm font-semibold ${isActive ? 'text-primary' : 'text-gray-800 dark:text-white'}`}>
                    {s.fullName}
                  </p>
                  <p className="text-xs text-gray-400">{cls?.name ?? s.currentClass}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Child header card */}
      {child && (
        <SoftCard>
          <div className="flex items-center gap-4">
            <StudentAvatar student={child} size="lg" />
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{child.fullName}</h2>
              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium text-xs">
                  <GraduationCap size={12} />
                  {classObj?.name ?? child.currentClass}
                </span>
                {child.admissionNumber && <span>Adm: {child.admissionNumber}</span>}
                <span>{child.gender === 'F' ? 'Female' : 'Male'}</span>
              </div>
            </div>
          </div>
        </SoftCard>
      )}

      {/* Add another child (sibling) by registration code */}
      <AddChildCard />

      {/* Phase 4: soft-gate prompt to complete missing guardian-contact fields */}
      <GuardianContactPrompt />

      {/* No photo of this child yet: the way to the upload, before anything else (Oct 2026). */}
      {child && activeTab !== 'profile' && (
        <PhotoNeededCard students={[child]} onAdd={() => setActiveTab('profile')} />
      )}

      {/* Tab bar */}
      <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          const badge = t.id === 'notices' ? noticesCtl.unreadCount : 0;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`relative flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'seg-on'
                  : 'seg-off'
              }`}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{t.label}</span>
              {badge > 0 && (
                <span className="absolute top-1 right-1 sm:static sm:ml-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-primaryLight text-white text-[10px] font-semibold leading-none">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'notices' ? (
        <div className="space-y-3">
          {/* Where a parent is already reading school messages is the one
              place asking to send them more actually makes sense. */}
          <ParentPushPrompt />
          <ParentNoticesFeed controller={noticesCtl} />
        </div>
      ) : activeTab === 'profile' ? (
        /* THE PROFILE IS NOT ACADEMIC (the school, 26 Oct 2026). The photo
           and the child's details stay open on a fees hold — 327 of the 330
           held pupils had no photo, and their parents, emailed every week to
           add one, found the whole child paused. It needs nothing from the
           academics call, so it never waits on it either. */
        renderProfile()
      ) : activeTab === 'timetable' ? (
        /* The child's class timetable (31 Oct 2026). Like Exams and Books it is
           not an academic result, so the fees hold does not hide it. */
        <MyTimetable bare />
      ) : activeTab === 'exams' && child ? (
        /* WHEN TO BE IN THE EXAM ROOM is not an academic result: the fees hold
           does not hide it (the school's hold pauses academics, not logistics).
           Results still come only through getChildAcademics. */
        <div className="study-theme">
          <ExamTimetableView
            sittings={exams.sittings}
            loading={exams.loading}
            error={exams.error}
            now={examClock}
            who={child.fullName.split(' ')[0]}
            title={exams.series.map((x) => `${child.fullName.split(' ')[0]}'s ${x.title}`).join(' · ') || undefined}
            print={{
              pupilName: child.fullName,
              className: classObj?.name,
              candidateNumber: exams.candidateNumber,
              seriesTitle: exams.series.map((x) => x.title).join(' · ') || 'Exam timetable',
            }}
          />
        </div>
      ) : activeTab === 'subjects' && child && classObj && choiceLevel ? (
        /* Choosing next year's subjects is not an academic result either: the
           fees hold does not hide it, or a held family would miss the window. */
        <SubjectChoices child={child} className={classObj.name} level={choiceLevel} settings={choiceSettings} />
      ) : activeTab === 'books' ? (
        /* BOOKS ARE NOT ACADEMIC EITHER (the school, 30 Oct 2026): a book on
           loan is owed, so the fees hold does not hide it. Read straight from
           textbookLoans, never through getChildAcademics. */
        <SoftCard><PupilBooks studentId={selectedChild ?? undefined} /></SoftCard>
      ) : childHeld ? (
        /* PER CHILD. The switcher above stays, so a parent with one child in
           arrears and one paid up reaches the second child's reports in a tap
           — and can still open Notices, and is still told where to pay. A
           blanket block would have hidden the way out along with the work. */
        <div className="space-y-4">
          <ParentHoldNotice />
          <SoftCard>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              While academic access is paused you can still add or change {child?.fullName ?? 'your child'}&apos;s photo and see their details.
            </p>
            <button
              onClick={() => setActiveTab('profile')}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[var(--accent-strong)] text-white hover:opacity-90"
            >
              Open photo &amp; details
            </button>
          </SoftCard>
        </div>
      ) : loading ? (
        <SoftCard>
          <div className="flex justify-center py-8">
            <Spinner label="Loading…" />
          </div>
        </SoftCard>
      ) : loadError ? (
        /* SAY WHAT HAPPENED. Before this, a failed call fell through to the
           tabs and every one of them rendered its own "nothing here yet" —
           which reads as the school having no reports, no marks and no
           attendance for the child, rather than as a page that did not
           load. */
        <SoftCard>
          <div className="flex items-start gap-3 py-2">
            <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                We could not load this page
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{loadError}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-3 text-sm font-medium text-primaryLight hover:underline"
              >
                Try again
              </button>
            </div>
          </div>
        </SoftCard>
      ) : (
        <>
          {activeTab === 'academic' && child && (
            <AcademicPerformance
              student={child}
              subjects={subjects}
              teachers={teachers}
              marksDocs={marksDocs}
              termStatuses={termStatuses}
              classAvgBySubject={classAvgBySubject}
              mode="parent"
            />
          )}
          {activeTab === 'homework' && renderHomework()}
          {activeTab === 'reports' && renderReports()}
          {activeTab === 'attendance' && renderAttendance()}
          {activeTab === 'achievements' && renderAchievements()}
          {activeTab === 'discipline' && showDiscipline && renderDiscipline()}
          {activeTab === 'documents' && child && (
            <SoftCard>
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                <FileText size={16} className="text-primaryLight" /> Documents
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Documents the school holds on file for {child.fullName}.
              </p>
              <DocumentsTab studentId={child.id} readOnly />
            </SoftCard>
          )}
          {activeTab === 'messages' && child && (
            <ParentMessages child={child} subjects={subjects} />
          )}
        </>
      )}
    </div>
  );

  /* ================================================================
     TAB: HOMEWORK — what the child's teachers have set (getChildAcademics
     hands it over without the class roster; lib/childHomework.js).
     Ported from Al-Nur 2026-10-30.
     ================================================================ */
  function renderHomework() {
    // Gaborone's date, so a due date does not tip over at 22:00 UTC.
    const today = new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 10);
    const due = homework.filter((h) => h.dueDate >= today);
    const past = homework.filter((h) => h.dueDate < today).reverse();
    if (!homework.length) {
      return (
        <SoftCard>
          <p className="py-4 text-center text-sm text-gray-600 dark:text-gray-300">
            No homework set in the last two weeks.
          </p>
        </SoftCard>
      );
    }
    const row = (h: ParentHomework) => {
      const done = h.ticked || h.handedIn;
      const state = h.ticked ? 'Marked done by the teacher' : h.handedIn ? 'Handed in' : h.dueDate < today ? 'Not handed in' : 'Not done yet';
      return (
        <li key={h.id} className="py-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{h.title}</p>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              {h.subjectName || 'Homework'}{h.setByName ? ` · ${h.setByName}` : ''} · due {longDate(h.dueDate)}
            </p>
            {h.instructions && <p className="mt-1 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{h.instructions}</p>}
          </div>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${done
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
            : h.dueDate < today
              ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
              : 'bg-sky-50 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'}`}
          >{state}</span>
        </li>
      );
    };
    return (
      <div className="space-y-4">
        {due.length > 0 && (
          <SoftCard>
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">Coming up</h3>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">{due.map(row)}</ul>
          </SoftCard>
        )}
        {past.length > 0 && (
          <SoftCard>
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">Recently due</h3>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">{past.map(row)}</ul>
          </SoftCard>
        )}
      </div>
    );
  }

  /* ================================================================
     TAB: REPORTS
     ================================================================ */
  function renderReports() {
    if (reports.length === 0) {
      return (
        <SoftCard>
          <p className="text-gray-500 text-sm text-center py-4">
            No reports available yet for this student.
          </p>
        </SoftCard>
      );
    }

    // Group by year + term
    const grouped: Record<string, GeneratedReport[]> = {};
    for (const r of reports) {
      const key = `${r.year} · ${(r.term ?? '').replace('Term', 'Term ')}`;
      (grouped[key] ?? (grouped[key] = [])).push(r);
    }

    return (
      <div className="space-y-4">
        {Object.entries(grouped).map(([label, reps]) => (
          <SoftCard key={label}>
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-3">{label}</h3>
            <div className="space-y-2">
              {reps.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  <div className="flex items-center gap-3">
                    <FileText size={16} className="text-primary" />
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-white">
                        Progress Report
                      </p>
                      <p className="text-xs text-gray-400">
                        Generated {new Date(r.generatedAt ?? '').toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.storagePath && (
                      <button
                        onClick={() => window.open(r.downloadUrl, '_blank')}
                        className="flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <Download size={14} /> Download
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </SoftCard>
        ))}
      </div>
    );
  }

  /* ================================================================
     TAB: ATTENDANCE
     ================================================================ */
  function renderAttendance() {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatChip tone="emerald" value={attendanceStats.present} label="Days Present" icon={Calendar} />
          <StatChip tone="red" value={attendanceStats.absent} label="Days Absent" icon={Calendar} />
          <StatChip tone="blue" value={attendanceStats.total} label="Total Days" icon={Calendar} />
          <StatChip tone="gold" value={`${attendanceStats.rate}%`} label="Attendance Rate" icon={Calendar} />
        </div>

        <SoftCard>
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-3">Attendance Rate</h3>
          <div className="h-3 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                attendanceStats.rate >= 90
                  ? 'bg-emerald-500'
                  : attendanceStats.rate >= 75
                  ? 'bg-amber-500'
                  : 'bg-red-500'
              }`}
              style={{ width: `${attendanceStats.rate}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {attendanceStats.rate >= 90
              ? 'Excellent attendance.'
              : attendanceStats.rate >= 75
              ? 'Attendance needs improvement.'
              : 'Attendance is a concern. Please contact the school.'}
          </p>
        </SoftCard>
      </div>
    );
  }

  /* ================================================================
     TAB: PROFILE
     ================================================================ */
  function renderProfile() {
    if (!child) return null;
    const admissions = child.admissions;
    const subjectName = (id: string) => subjects.find((s) => s.id === id)?.name ?? id;

    return (
      <div className="space-y-4">
        <ChildPhotoCard child={child} />
        <SoftCard>
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-3">Personal Information</h3>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
              <span className="text-gray-500">Full Name</span>
              <span className="font-medium text-gray-800 dark:text-white">{child.fullName}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
              <span className="text-gray-500">Gender</span>
              <span className="font-medium">{child.gender === 'F' ? 'Female' : 'Male'}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
              <span className="text-gray-500">Class</span>
              <span className="font-medium">{classObj?.name ?? child.currentClass}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
              <span className="text-gray-500">Admission #</span>
              <span className="font-medium">{child.admissionNumber ?? '—'}</span>
            </div>
            {(child.dateOfBirth ?? admissions?.dateOfBirth) && (
              <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
                <span className="text-gray-500">Date of Birth</span>
                <span className="font-medium">{child.dateOfBirth ?? admissions?.dateOfBirth}</span>
              </div>
            )}
            {admissions?.nationality && (
              <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
                <span className="text-gray-500">Nationality</span>
                <span className="font-medium">{admissions.nationality}</span>
              </div>
            )}
          </div>
        </SoftCard>

        {child.subjects && child.subjects.length > 0 && (
          <SoftCard>
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-3">
              Subjects ({child.subjects.length})
            </h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {child.subjects.map((sid) => (
                <div
                  key={sid}
                  className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 dark:border-gray-700 text-sm"
                >
                  <span className="font-medium">{subjectName(sid)}</span>
                  <span className="text-xs text-gray-400">
                    {teachers.find((t) => t.id === child.subjectTeachers?.[sid])?.displayName ?? '—'}
                  </span>
                </div>
              ))}
            </div>
          </SoftCard>
        )}

        {admissions?.medical && (
          <SoftCard>
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-3">Medical Information</h3>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              {admissions.medical.chronicConditions && (
                <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
                  <span className="text-gray-500">Conditions</span>
                  <span className="font-medium">{admissions.medical.chronicConditions}</span>
                </div>
              )}
              {admissions.medical.allergies && (
                <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
                  <span className="text-gray-500">Allergies</span>
                  <span className="font-medium">{admissions.medical.allergies}</span>
                </div>
              )}
              {admissions.medical.bloodType && (
                <div className="flex justify-between py-1.5 border-b border-gray-50 dark:border-gray-700">
                  <span className="text-gray-500">Blood Group</span>
                  <span className="font-medium">{admissions.medical.bloodType}</span>
                </div>
              )}
            </div>
          </SoftCard>
        )}
      </div>
    );
  }

  /* ================================================================
     TAB: DISCIPLINE
     ================================================================ */
  function renderAchievements() {
    if (achievementRecords.length === 0) {
      return (
        <SoftCard>
          <p className="text-gray-500 text-sm text-center py-4">
            No achievements recorded yet — they'll appear here as your child earns them.
          </p>
        </SoftCard>
      );
    }
    const LEVEL_STYLE: Record<string, string> = {
      class: 'bg-slate-100 text-slate-700',
      school: 'bg-blue-100 text-blue-700',
      zonal: 'bg-teal-100 text-teal-700',
      regional: 'bg-emerald-100 text-emerald-700',
      national: 'bg-amber-100 text-amber-800',
      international: 'bg-yellow-100 text-yellow-800',
    };
    return (
      <div className="space-y-3">
        {achievementRecords.map((rec) => (
          <SoftCard key={rec.id}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                <Award size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${LEVEL_STYLE[rec.level] ?? 'bg-slate-100 text-slate-700'}`}>
                    {rec.level}
                  </span>
                  <span className="text-xs text-gray-400 capitalize">{rec.category.replace(/_/g, ' ')}</span>
                  {typeof rec.points === 'number' && (
                    <span className="text-xs font-semibold text-amber-600">{rec.points} pts</span>
                  )}
                </div>
                <p className="text-sm font-medium text-gray-800 dark:text-white">{rec.title}</p>
                {rec.description && <p className="text-xs text-gray-500 mt-0.5">{rec.description}</p>}
                <p className="text-xs text-gray-400 mt-1">{rec.awardDate}</p>
              </div>
            </div>
          </SoftCard>
        ))}
      </div>
    );
  }

  function renderDiscipline() {
    if (disciplineRecords.length === 0) {
      return (
        <SoftCard>
          <p className="text-gray-500 text-sm text-center py-4">
            No discipline records for this student.
          </p>
        </SoftCard>
      );
    }

    return (
      <div className="space-y-3">
        {disciplineRecords.map((rec) => (
          <SoftCard key={rec.id}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    rec.severity === 'critical' ? 'bg-red-100 text-red-700' :
                    rec.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                    rec.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {rec.severity}
                  </span>
                  <span className="text-xs text-gray-400 capitalize">
                    {rec.category.replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="text-sm text-gray-800 dark:text-white">{rec.description}</p>
                {rec.actionTaken && (
                  <p className="text-xs text-gray-500 mt-1">Action: {rec.actionTaken}</p>
                )}
              </div>
              <div className="text-right shrink-0 ml-4">
                <p className="text-xs text-gray-400">{rec.incidentDate}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  rec.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                  rec.status === 'escalated' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {rec.status}
                </span>
              </div>
            </div>
          </SoftCard>
        ))}
      </div>
    );
  }
}
